/**
 * Route chunks that survive a deploy.
 *
 * Every build renames its hashed chunks. A tab opened before a deploy still
 * holds the old index.html, so the first route it visits afterwards asks for
 * a chunk that no longer exists and the page goes blank ("the app isn't
 * loading", 8 Sep 2026 — after eight deploys in a day). When a chunk fails
 * to load, reload the page once so the new index.html and its chunks come
 * down; a second failure in a row is a real fault and is thrown as such.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'bsa.chunk-reload';
/**
 * Long enough that a genuinely broken chunk cannot spin, short enough that a
 * person working through a day of deploys always recovers by themselves.
 *
 * The mark used to be the chunk's NAME, one reload per name per session. That
 * holds for one deploy. It does not hold for a day of them: a tab left open
 * across several deploys asks for a stale chunk, recovers, then asks for
 * another stale one later and is refused a second reload because the mark is
 * still sitting there — the page simply stops (15 Sep 2026, eight deploys in a
 * day, "neeraj portal stopped working"). Time is the honest guard: recover as
 * often as needed, never twice inside the window.
 */
const RELOAD_WINDOW_MS = 30_000;
/** How long the reload waits, so it does not land while the update swaps the web container. */
const RELOAD_DELAY_MS = 3_000;

function markedReloaded(_name: string): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (!Number.isFinite(at) || at <= 0) return false;
    return Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function markReloaded(_name: string): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: still reload. Without the mark a broken chunk could
    // loop, so the reload below is the only attempt such a browser gets.
  }
}

export function clearChunkReloadMark(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * `lazy()` that reloads the page once when its chunk is gone after a deploy.
 * The constraint is React's own for `lazy()`, so a page that takes props
 * (DeskPage's `kind`) fits as well as one that takes none; the props type
 * survives on the result.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRetry<T extends ComponentType<any>>(
  name: string,
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const loaded = await factory();
      clearChunkReloadMark();
      return loaded;
    } catch (error) {
      if (!markedReloaded(name)) {
        markReloaded(name);
        // A few seconds' grace (9 Oct 2026): the chunk is usually missing because
        // an update is swapping the web container, and a reload at once can land
        // in the gap and show the proxy's bare "404 page not found".
        window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
        // Hold the render until the reload lands.
        await new Promise<never>(() => undefined);
      }
      throw error;
    }
  });
}
